'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';

import Link from '@eliadmin/core/src/components/link';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { IconButton } from '@eliadmin/core/src/components/nt/ui/icon-button';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/** Figma „Overlay“ (223:14472): #0D110D at 50 %, no blur – as the review modal. */
const OVERLAY = cn(
  'fixed inset-0 z-50 bg-[#0D110D]/50',
  'data-[state=open]:animate-in data-[state=open]:fade-in-0',
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 motion-reduce:animate-none',
);

/*
 * Phone (290:34891): a full-width sheet on the bottom edge, radius 16 on top, padding 24 / 16. From
 * sm the Figma card (223:14538): 652 wide, radius 16, padding 40, 24 between the rows, shadow
 * 0 16 48 −8 #0D120D 20 %, centred with `inset-0 m-auto` (no translate, so the zoom animation keeps
 * the centre). It scrolls inside when taller than the viewport.
 */
const CONTENT = cn(
  'fixed z-50 flex flex-col gap-6 overflow-y-auto overscroll-contain bg-white outline-none',
  'shadow-[0_16px_48px_-8px_rgba(13,18,13,0.2)]',
  'max-sm:inset-x-0 max-sm:bottom-0 max-sm:max-h-[calc(100dvh-16px)] max-sm:rounded-t-2xl',
  'max-sm:px-4 max-sm:pb-[calc(1.5rem+env(safe-area-inset-bottom))] max-sm:pt-6',
  'sm:inset-0 sm:m-auto sm:h-fit sm:max-h-[calc(100dvh-32px)] sm:w-[652px] sm:max-w-[calc(100vw-32px)] sm:rounded-2xl sm:p-10',
  'data-[state=open]:animate-in data-[state=open]:fade-in-0',
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
  'max-sm:data-[state=open]:slide-in-from-bottom max-sm:data-[state=closed]:slide-out-to-bottom',
  'sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95 motion-reduce:animate-none',
);

/**
 * The modal of the auth pages (Figma „Modal“ 223:14538 … 223:14931) inside a Radix `Dialog` root –
 * the page's own or the core dialog slot (`StaySignInDialog`). The ghost „Zavrieť“ (48 px) sits
 * 16 / 24 px from the top right corner (8 / 8 on the phone) and comes last in the tab order. Radix
 * keeps the focus inside, Escape and the overlay close it.
 */
export function AuthDialogContent({
  closeLabel,
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { closeLabel: string }) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={OVERLAY} />
      <DialogPrimitive.Content className={cn(CONTENT, className)} {...props}>
        {children}
        <DialogPrimitive.Close asChild>
          <IconButton
            icon="x"
            label={closeLabel}
            variant="ghost"
            className="absolute right-2 top-2 sm:right-6 sm:top-4"
          />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

const TEXT_LINK =
  'nt-button-m inline-flex items-center gap-1 rounded text-[#0E6A20] underline-offset-2 transition-colors hover:text-[#0B5719] hover:underline focus-visible:shadow-focus focus-visible:outline-none';

/**
 * Figma „Link“ of the modals („Späť na prihlásenie ›“, „Zadať iný e-mail ›“, „Pokračovať v nákupe ›“):
 * UI/Button M #0E6A20 with the 14 px caret 4 px after it – a button, or a link with `href`.
 */
export function DialogTextLink({
  children,
  href,
  onClick,
}: {
  children: ReactNode;
  href?: string;
  onClick?: () => void;
}) {
  const content = (
    <>
      <span>{children}</span>
      <Icon name="caret-right" size={14} />
    </>
  );
  if (href) {
    return (
      <Link href={href} className={TEXT_LINK}>
        {content}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={TEXT_LINK}>
      {content}
    </button>
  );
}
